function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function parseCustomDiscountAmount(customAmount) {
  if (customAmount === "" || customAmount === null || customAmount === undefined) {
    return null;
  }

  const amount = parseFloat(customAmount);
  if (Number.isNaN(amount) || amount < 0) return null;

  return roundMoney(amount);
}

export function isValidCustomDiscountAmount(customAmount) {
  return parseCustomDiscountAmount(customAmount) !== null;
}

export function formatInvoiceDiscountLabel(rule) {
  if (!rule) return "Discount";
  const title = rule.title || rule.name || "Discount";
  if (rule.percentage) {
    return `${title} (${rule.percentage}%)`;
  }
  if (rule.fixed_amount) {
    return `${title} (${rule.fixed_amount})`;
  }
  return title;
}

export function calculateInvoiceDiscountAmount({
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  grossSubtotal = 0,
  maxTotal = 0,
}) {
  const cap = Math.max(Number(maxTotal) || 0, 0);
  const gross = Math.max(Number(grossSubtotal) || 0, 0);

  if (tab === "predefined") {
    const rule = (rules || []).find((item) => String(item.id) === String(ruleId));
    if (!rule) return 0;

    let amount = 0;
    if (rule.percentage) {
      amount = gross * (Number(rule.percentage) / 100);
    } else {
      amount = Number(rule.fixed_amount) || 0;
    }

    return Math.min(roundMoney(amount), cap);
  }

  if (tab === "custom") {
    const amount = parseCustomDiscountAmount(customAmount);
    if (amount === null) return 0;
    return Math.min(amount, cap);
  }

  return 0;
}

function calculateUncheckedInvoiceDiscountAmount({
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  grossSubtotal = 0,
}) {
  const gross = Math.max(Number(grossSubtotal) || 0, 0);

  if (tab === "predefined") {
    const rule = (rules || []).find((item) => String(item.id) === String(ruleId));
    if (!rule) return 0;

    if (rule.percentage) {
      return roundMoney(gross * (Number(rule.percentage) / 100));
    }

    return roundMoney(Number(rule.fixed_amount) || 0);
  }

  if (tab === "custom") {
    const amount = parseCustomDiscountAmount(customAmount);
    if (amount === null) return 0;
    return amount;
  }

  return 0;
}

export function validateInvoiceDiscountExceedsTotal({
  tab,
  ruleId,
  customAmount,
  grossSubtotal,
  maxTotal = 0,
  rules = [],
}) {
  const maxAllowed = Math.max(Number(maxTotal) || 0, 0);
  if (maxAllowed <= 0) return false;

  const requestedAmount = calculateUncheckedInvoiceDiscountAmount({
    tab,
    ruleId,
    rules,
    customAmount,
    grossSubtotal,
  });

  return requestedAmount > maxAllowed;
}

export function getInvoiceDiscountExceedsTotalMessage(maxTotal = 0, currency = "") {
  const formattedTotal = Number(maxTotal || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const prefix = currency ? `${currency} ` : "";
  return `Discount cannot exceed the invoice total (${prefix}${formattedTotal}).`;
}

export function buildInvoiceDiscountPayload({
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  reason = "",
  grossSubtotal = 0,
  maxTotal = 0,
}) {
  const description = String(reason || "").trim();
  if (!description) return null;

  const amount = calculateInvoiceDiscountAmount({
    tab,
    ruleId,
    rules,
    customAmount,
    grossSubtotal,
    maxTotal,
  });

  if (amount < 0) return null;

  if (tab === "predefined") {
    const rule = (rules || []).find((item) => String(item.id) === String(ruleId));
    if (!rule) return null;

    return {
      amount,
      description,
    };
  }

  if (tab === "custom") {
    if (parseCustomDiscountAmount(customAmount) === null) return null;

    return {
      amount,
      description,
    };
  }

  return null;
}

export function buildInvoiceDiscountApiPayload({
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  reason = "",
  grossSubtotal = 0,
  maxTotal = 0,
}) {
  const description = String(reason || "").trim();
  if (!description) return null;

  const amount = calculateInvoiceDiscountAmount({
    tab,
    ruleId,
    rules,
    customAmount,
    grossSubtotal,
    maxTotal,
  });

  if (amount < 0) return null;

  if (tab === "predefined") {
    const rule = (rules || []).find((item) => String(item.id) === String(ruleId));
    if (!rule) return null;

    const discountType = rule.percentage ? "percentage" : "fixed";

    return {
      type: discountType,
      percentage: rule.percentage ? Number(rule.percentage) : null,
      fixed_amount:
        discountType === "fixed"
          ? Number(rule.fixed_amount) || amount
          : null,
      description,
      amount,
      rule_id: rule.id,
    };
  }

  if (tab === "custom") {
    return {
      type: "fixed",
      percentage: null,
      fixed_amount: amount,
      description,
      amount,
      rule_id: null,
    };
  }

  return null;
}

export function mapInvoiceDiscountToFormState({
  amount = 0,
  reason = "",
  ruleId = "",
} = {}) {
  const normalizedAmount = Number(amount) || 0;
  const normalizedReason = String(reason || "").trim();
  const normalizedRuleId = ruleId ? String(ruleId) : "";

  if (normalizedAmount <= 0 && !normalizedReason) {
    return {
      tab: "predefined",
      ruleId: "",
      customAmount: "",
      reason: "",
    };
  }

  if (normalizedRuleId) {
    return {
      tab: "predefined",
      ruleId: normalizedRuleId,
      customAmount: "",
      reason: normalizedReason,
    };
  }

  return {
    tab: "custom",
    ruleId: "",
    customAmount: String(normalizedAmount),
    reason: normalizedReason,
  };
}

export function hasInvoiceDiscountChanged(initial, current = {}) {
  const base = initial || {};
  const initialAmount = roundMoney(base.amount || 0);
  const currentAmount = roundMoney(current.amount || 0);
  const initialReason = String(base.reason || "").trim();
  const currentReason = String(current.reason || "").trim();
  const initialTab = String(base.tab || "predefined");
  const currentTab = String(current.tab || "predefined");
  const initialRuleId = String(base.ruleId || "");
  const currentRuleId = String(current.ruleId || "");

  return (
    initialAmount !== currentAmount ||
    initialReason !== currentReason ||
    initialTab !== currentTab ||
    initialRuleId !== currentRuleId
  );
}

export function mergeInvoiceDiscountIntoTotals(lineTotals, invoiceDiscount) {
  if (!lineTotals) return lineTotals;

  const discountAmount = Math.max(Number(invoiceDiscount?.amount) || 0, 0);
  if (discountAmount <= 0) {
    return {
      ...lineTotals,
      invoice_discount_amount: 0,
    };
  }

  const totalBeforeInvoiceDiscount = Math.max(
    Number(lineTotals.total_amount) || 0,
    0,
  );
  const appliedDiscount = Math.min(discountAmount, totalBeforeInvoiceDiscount);
  const totalAmount = roundMoney(totalBeforeInvoiceDiscount - appliedDiscount);

  return {
    ...lineTotals,
    invoice_discount_amount: appliedDiscount,
    discount_total: roundMoney(appliedDiscount),
    total_amount: totalAmount,
    amount_due: totalAmount,
  };
}

export function validateInvoiceDiscountForm({
  tab,
  ruleId,
  customAmount,
  reason,
  grossSubtotal,
  maxTotal = 0,
  rules = [],
  currency = "",
}) {
  const description = String(reason || "").trim();
  const hasRule = Boolean(ruleId);
  const hasCustomAmount = isValidCustomDiscountAmount(customAmount);
  const wantsDiscount = hasRule || hasCustomAmount || Boolean(description);

  if (!wantsDiscount) {
    return { valid: true, invoiceDiscount: null, error: "" };
  }

  if (!description) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: "Discount reason is required.",
    };
  }

  if (tab === "predefined" && !hasRule) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: "Please select a discount rule.",
    };
  }

  if (tab === "custom" && !hasCustomAmount) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: "Enter a valid custom discount amount (0 or greater).",
    };
  }

  if ((Number(grossSubtotal) || 0) <= 0) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: "Add line items before applying a discount.",
    };
  }

  const maxAllowed = Math.max(Number(maxTotal) || 0, 0);
  if (
    validateInvoiceDiscountExceedsTotal({
      tab,
      ruleId,
      customAmount,
      grossSubtotal,
      maxTotal: maxAllowed,
      rules,
    })
  ) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: getInvoiceDiscountExceedsTotalMessage(maxAllowed, currency),
    };
  }

  const invoiceDiscount = buildInvoiceDiscountPayload({
    tab,
    ruleId,
    rules,
    customAmount,
    reason: description,
    grossSubtotal,
    maxTotal,
  });

  if (!invoiceDiscount) {
    return {
      valid: false,
      invoiceDiscount: null,
      error: "Unable to calculate discount for this invoice.",
    };
  }

  return { valid: true, invoiceDiscount, error: "" };
}

export function buildInvoiceApplyDiscountRequest({
  invoiceId,
  invoiceItemId,
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  reason = "",
  grossSubtotal = 0,
  maxTotal = 0,
}) {
  const apiPayload = buildInvoiceDiscountApiPayload({
    tab,
    ruleId,
    rules,
    customAmount,
    reason,
    grossSubtotal,
    maxTotal,
  });

  if (!apiPayload || !invoiceId || !invoiceItemId) {
    return null;
  }

  return {
    invoice_id: invoiceId,
    invoice_item_id: invoiceItemId,
    type: apiPayload.type,
    percentage: apiPayload.percentage,
    fixed_amount: apiPayload.fixed_amount,
    description: apiPayload.description,
    discount_amount: apiPayload.amount,
    discount_reason: apiPayload.description,
  };
}

export function buildInvoiceUpdateDiscountRequest({
  invoiceId,
  invoiceItemId,
  discountTransactionId,
  tab = "predefined",
  ruleId = "",
  rules = [],
  customAmount = "",
  reason = "",
  grossSubtotal = 0,
  maxTotal = 0,
}) {
  const discountReason = String(reason || "").trim();
  const discountPrice = calculateInvoiceDiscountAmount({
    tab,
    ruleId,
    rules,
    customAmount,
    grossSubtotal,
    maxTotal,
  });

  if (
    !invoiceId ||
    !invoiceItemId ||
    !discountTransactionId ||
    !discountReason ||
    discountPrice < 0 ||
    Number.isNaN(discountPrice)
  ) {
    return null;
  }

  return {
    invoice_id: invoiceId,
    invoice_item_id: invoiceItemId,
    discount_transaction_id: discountTransactionId,
    discount_reason: discountReason,
    discount_price: discountPrice,
  };
}
